import { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { jwtDecode } from 'jwt-decode';

import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import {
	setErrorMessage,
	setIsAuthenticated,
	setIsError,
	setLoggedInUser,
} from 'common/store/reducer/commonReducer';
import { storageCleanAndRedirect } from 'common/utils/authCheck';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { getUserInfo } from 'features/Settings/UserManagement/api';

import { Loader } from '../../components/Loader/Loader';
import AlertComponent from '../../components/ToastMessage/ToastMessage';
import { RootState } from '../../store/AppStore';

import authStyles from './Auth.module.scss';

const Auth = () => {
	const { OK, BAD_REQUEST, NOT_FOUND, FORBIDDEN } = HTTP_STATUS_CODES;
	const dispatch = useDispatch();
	const [urlParams] = useSearchParams();
	const location = useLocation();
	const navigate = useNavigate();

	const isError = useSelector((state: RootState) => state.common.isError);
	const returnTo = useSelector((state: RootState) => state.common.returnTo);
	const errorMessage = useSelector(
		(state: RootState) => state.common.errorMessage,
	);

	const isAzureADAuthenticated = true;

	const handleToast = useCallback(() => {
		dispatch(setIsError(false));
		dispatch(setErrorMessage(''));
	}, []);
	const navigateToAuthenticatedRoute = () => {
		if (!returnTo) {
			navigate(
				window.location.pathname !== urlWithTenant('/callback')
					? window.location.pathname
					: urlWithTenant('/home'),
			);
		} else {
			const navigateTo = returnTo.split(window.location.origin)[1];
			navigate(navigateTo);
		}
	};
	// function to fetch user details once token is available in sessionStorage
	const getUserDetails = async () => {
		const response = await getUserInfo();
		if (response?.statusCode === OK && response.responseBody) {
			storageWrapper.setStorageItem(
				'user',
				JSON.stringify(response.responseBody),
			);
			storageWrapper.setStorageItem('loginState', true);
			dispatch(setLoggedInUser(response.responseBody));
			dispatch(setIsAuthenticated(true));
			navigateToAuthenticatedRoute();
		} else {
			storageWrapper.setStorageItem('user', '{}');
			dispatch(setLoggedInUser(null));
			storageWrapper.setStorageItem('loginState', false);
			if (response?.statusCode === BAD_REQUEST) {
				dispatch(setErrorMessage('Bad request, Please try again later.'));
			} else if (
				response?.statusCode === NOT_FOUND ||
				response?.statusCode === FORBIDDEN
			) {
				dispatch(setErrorMessage('User Not Found in RRCP Platform!'));
			} else {
				dispatch(setErrorMessage(APP_GENERAL_ERROR_MSG));
			}
			dispatch(setIsError(true));
			dispatch(setIsAuthenticated(false));
		}
	};

	useEffect(() => {
		if (urlParams !== undefined) {
			const queryParams = new URLSearchParams(location.search);
			try {
				const token = queryParams.get('id-token');
				if (token) {
					const decodedPayload = jwtDecode(token);
					storageWrapper.setStorageItem('access-token', token);
					storageWrapper.setStorageItem('id-token', token);
					storageWrapper.setStorageItem('loginState', true);
					storageWrapper.setStorageItem('expiry', decodedPayload?.exp);
					getUserDetails();
				} else {
					storageCleanAndRedirect();
				}
			} catch (err) {
				storageCleanAndRedirect();
			}
		}
	}, [location]);

	return (
		<div className={authStyles['auth-container']}>
			<div
				data-testid="loader-component"
				className={authStyles['auth-loader-container']}
			>
				<Loader show={!isAzureADAuthenticated} />
			</div>
			<AlertComponent
				open={isError}
				toastMessage={errorMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="center"
			/>
		</div>
	);
};

export default Auth;
